import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  CheckCircle,
  Loader2,
  Download,
  ArrowLeft,
  Receipt
} from 'lucide-react';
import { toast } from 'sonner';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await api.get(`/parent-portal/payment/status/${sessionId}`);
      setPaymentStatus(response.data);
      
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful!');
      }
    } catch (error) {
      console.error('Failed to check payment status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#1e3a5f] mx-auto mb-4" />
          <p className="text-slate-600">Verifying payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto py-12">
      <Card>
        <CardContent className="pt-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>

          <h1 className="text-2xl font-bold text-[#1e3a5f] mb-2">Payment Successful!</h1>
          
          <p className="text-slate-600 mb-6">
            Thank you for your payment. Your receipt has been generated and is available for download.
          </p>

          {paymentStatus && (
            <div className="p-4 bg-slate-50 rounded-lg mb-6">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-slate-600">Amount Paid:</span>
                <span className="font-bold text-[#1e3a5f]">
                  £{paymentStatus.amount?.toFixed(2)} {paymentStatus.currency?.toUpperCase()}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Status:</span>
                <span className="font-medium text-green-600 capitalize">{paymentStatus.payment_status}</span>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <Button 
              onClick={() => navigate('/parent-portal')} 
              className="w-full bg-[#1e3a5f]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>

            <Button 
              variant="outline" 
              onClick={() => navigate('/parent-portal?tab=payments')}
              className="w-full"
            >
              <Receipt className="w-4 h-4 mr-2" />
              View Receipts
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
